/**
 * Billing plans — single source of truth.
 *
 * Every quota, feature gate, validator list, Stripe price mapping,
 * pricing display value, and UI label in the app derives from this file.
 * To add a tier, change a limit, or toggle a feature: edit the PLANS
 * record below — every consumer re-derives automatically.
 *
 * Layering note: this file owns **plan-aware entitlements** (quotas,
 * features, pricing). Per-request abuse shields live in
 * `src/lib/rate-limit.ts` and are intentionally plan-independent.
 * Don't reach for rate limits to enforce a quota — that's what the
 * periodic quotas below are for.
 */
import { v } from 'convex/values';

// ─── Plan IDs (single source of truth for the plan union) ────────────────────
export const PLAN_IDS = ['free', 'pro', 'enterprise'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/**
 * Convex validator for every `plan` field across the schema.
 * Import this instead of redeclaring `v.union(v.literal('free'), ...)` —
 * the compile-time check below guarantees it stays in lockstep with PLAN_IDS.
 *
 * If you rename or remove a plan ID, existing rows in the DB must be
 * migrated BEFORE tightening this validator. See `billing/migrations.ts`
 * and deploy with `schemaValidation: false` during the transition.
 */
export const planIdValidator = v.union(
  v.literal('free'),
  v.literal('pro'),
  v.literal('enterprise'),
);

// If PLAN_IDS and planIdValidator drift, this fails typecheck.
type _ValidatorType = (typeof planIdValidator)['type'];
type _AssertExact<A, B> = [A] extends [B]
  ? [B] extends [A]
    ? true
    : never
  : never;
const _planIdValidatorMatchesPlanIds: _AssertExact<PlanId, _ValidatorType> =
  true;
void _planIdValidatorMatchesPlanIds;

// ─── Plan rank (free < pro < enterprise) — derived from PLAN_IDS order ───────
const PLAN_RANK: Record<PlanId, number> = Object.fromEntries(
  PLAN_IDS.map((id, i) => [id, i]),
) as Record<PlanId, number>;

export const isPlanAtLeast = (actual: PlanId, required: PlanId) =>
  PLAN_RANK[actual] >= PLAN_RANK[required];

// ─── Feature flags ───────────────────────────────────────────────────────────
export const FEATURES = [
  'githubIntegration', // GitHub Check Runs
  'prComments', // GitHub PR review comments
  'apiAccess', // Machine API (catalog, bundle, context, findings, enrichments)
  'cloudPolicies', // Cloud-managed governance rules
  'analytics', // Dashboard analytics & adoption tracking
  'policyManagement', // Cloud policy editor / rules management
  'auditLog', // Audit log UI
  'ssoSaml', // SSO / SAML
  'customValidators', // Custom governance rules / validators
  'requiredStatusChecks', // Governance as a required merge gate
] as const;
export type Feature = (typeof FEATURES)[number];

// ─── Quotas ──────────────────────────────────────────────────────────────────
export const QUOTAS = [
  'maxMembers',
  'maxProjects',
  'maxRepoBindings',
  'maxAiSuggestions', // per billing period
  'maxEnrichments', // per billing period (AI component enrichments)
  'maxAutofixPrs', // per billing period
] as const;
export type Quota = (typeof QUOTAS)[number];

/**
 * Maps periodic quotas to their `usageMeters.metric` key.
 * This is the authoritative list of periodic quotas — PERIODIC_QUOTAS
 * and MetricKey both derive from it so the three stay in lockstep.
 */
export const QUOTA_TO_METRIC = {
  maxAiSuggestions: 'ai_suggestions',
  maxEnrichments: 'enrichments',
  maxAutofixPrs: 'autofix_prs',
} as const satisfies Partial<Record<Quota, string>>;

/** Subset of Quota that is tracked per billing period. */
export type PeriodicQuota = keyof typeof QUOTA_TO_METRIC;

/** Union of metric strings stored in `usageMeters.metric`. */
export type MetricKey = (typeof QUOTA_TO_METRIC)[PeriodicQuota];

/** Quotas that reset each billing period (derived from QUOTA_TO_METRIC). */
export const PERIODIC_QUOTAS = Object.keys(QUOTA_TO_METRIC) as PeriodicQuota[];

// ─── Validators ──────────────────────────────────────────────────────────────
const ALL_VALIDATORS = [
  'safety',
  'a11y',
  'tokens',
  'styles',
  'components',
  'brand',
  'canonical-usage',
  'tailwind',
] as const;

// ─── Per-plan config ─────────────────────────────────────────────────────────
export interface PlanConfig {
  label: string;
  tagline: string;
  quotas: Record<Quota, number>; // Infinity = unlimited
  features: Record<Feature, boolean>;
  validators: readonly string[];
  /** Display pricing (per seat/month). Omit for free. */
  pricing?: {
    monthly: number;
    annual: number;
  };
  stripe?: {
    monthlyPriceEnvKey: string; // Convex env-var name holding the Stripe price ID
    annualPriceEnvKey: string;
  };
  trial?: { days: number };
}

export const PLANS: Record<PlanId, PlanConfig> = {
  free: {
    label: 'Free',
    tagline: 'Solo developers & evaluation',
    quotas: {
      maxMembers: 5,
      maxProjects: 1,
      maxRepoBindings: 1,
      maxAiSuggestions: 50,
      maxEnrichments: 25,
      maxAutofixPrs: 2,
    },
    features: {
      // Free is the local-governance hook: the in-app findings view + a
      // local CLI scan, with no hosted loop. The hosted GitHub/machine loop
      // — Check Runs, PR review comments, and the machine API (catalog /
      // bundle / context + CI ingest) — is the paid line, so the product we
      // sell starts at Pro. Volume caps (above) still bound the free tier.
      githubIntegration: false,
      prComments: false,
      apiAccess: false,
      cloudPolicies: false,
      analytics: false,
      policyManagement: false,
      auditLog: false,
      ssoSaml: false,
      customValidators: false,
      requiredStatusChecks: false,
    },
    validators: ALL_VALIDATORS,
  },

  pro: {
    label: 'Pro',
    tagline: 'For small teams & startups',
    quotas: {
      maxMembers: 15,
      maxProjects: 3,
      maxRepoBindings: 3,
      maxAiSuggestions: 500,
      maxEnrichments: 100,
      maxAutofixPrs: 25,
    },
    features: {
      githubIntegration: true,
      prComments: true,
      apiAccess: true,
      cloudPolicies: true,
      analytics: true,
      policyManagement: true,
      auditLog: false,
      ssoSaml: false,
      customValidators: false,
      requiredStatusChecks: false,
    },
    validators: ALL_VALIDATORS,
    pricing: { monthly: 39, annual: 32 },
    stripe: {
      monthlyPriceEnvKey: 'STRIPE_PRO_MONTHLY_PRICE_ID',
      annualPriceEnvKey: 'STRIPE_PRO_ANNUAL_PRICE_ID',
    },
    trial: { days: 14 },
  },

  enterprise: {
    label: 'Enterprise',
    tagline: 'Compliance, custom validators & SLA',
    quotas: {
      maxMembers: Infinity,
      maxProjects: Infinity,
      maxRepoBindings: Infinity,
      maxAiSuggestions: Infinity,
      maxEnrichments: Infinity,
      maxAutofixPrs: Infinity,
    },
    features: {
      githubIntegration: true,
      prComments: true,
      apiAccess: true,
      cloudPolicies: true,
      analytics: true,
      policyManagement: true,
      auditLog: true,
      ssoSaml: true,
      customValidators: true,
      requiredStatusChecks: true,
    },
    validators: ALL_VALIDATORS,
    // Custom-priced through sales. The Stripe config remains wired so a
    // closed deal can be provisioned via Checkout/manual subscription;
    // the pricing UI hides the number and routes prospects to mailto:.
    stripe: {
      monthlyPriceEnvKey: 'STRIPE_ENTERPRISE_MONTHLY_PRICE_ID',
      annualPriceEnvKey: 'STRIPE_ENTERPRISE_ANNUAL_PRICE_ID',
    },
    trial: { days: 14 },
  },
};

/**
 * Plans that are self-serve in the pricing/onboarding UI.
 * A plan is self-serve when it has a public list price — Enterprise
 * deliberately omits `pricing` so it drops out of this list.
 */
export const SELF_SERVE_PLAN_IDS: readonly PlanId[] = PLAN_IDS.filter(
  (id) => PLANS[id].pricing != null,
);

// ─── Derived helpers (lookups, not duplication) ──────────────────────────────
export const getPlan = (id: PlanId): PlanConfig => PLANS[id];
export const getQuota = (id: PlanId, q: Quota): number => PLANS[id].quotas[q];
export const hasFeature = (id: PlanId, f: Feature): boolean =>
  PLANS[id].features[f];
export const getValidators = (id: PlanId): readonly string[] =>
  PLANS[id].validators;
export const getStripeConfig = (id: PlanId) => PLANS[id].stripe;
export const getPricing = (id: PlanId) => PLANS[id].pricing;

/**
 * The cheapest plan that includes the given feature.
 * Used by upgrade prompts to tell the user which plan to jump to.
 * Returns `null` if no plan includes it (shouldn't happen in practice).
 */
export const cheapestPlanWithFeature = (f: Feature): PlanId | null =>
  PLAN_IDS.find((id) => PLANS[id].features[f]) ?? null;

export const hasGovernanceAutomation = (id: PlanId): boolean =>
  hasFeature(id, 'apiAccess') && hasFeature(id, 'githubIntegration');

export const cheapestPlanWithGovernanceAutomation = (): PlanId | null =>
  PLAN_IDS.find((id) => hasGovernanceAutomation(id)) ?? null;

export const hasCloudPolicyManagement = (id: PlanId): boolean =>
  hasFeature(id, 'cloudPolicies') && hasFeature(id, 'policyManagement');

export const cheapestPlanWithCloudPolicyManagement = (): PlanId | null =>
  PLAN_IDS.find((id) => hasCloudPolicyManagement(id)) ?? null;

/**
 * Compute the annual savings percentage for a plan.
 * Returns 0 if the plan has no pricing.
 */
export const getAnnualSavingsPercent = (id: PlanId): number => {
  const p = PLANS[id].pricing;
  if (!p) return 0;
  return Math.round(((p.monthly - p.annual) / p.monthly) * 100);
};

/** Map a Stripe subscription status to the plan the org should have. */
export function planForSubscriptionStatus(
  status: string,
  targetPlan: PlanId,
): PlanId {
  const ACTIVE_STATUSES = new Set(['active', 'trialing', 'past_due']);
  return ACTIVE_STATUSES.has(status) ? targetPlan : 'free';
}

/**
 * Given a Stripe price ID, resolve which plan it belongs to.
 * Reads Convex env vars named in each plan's `stripe` config.
 */
export function planForPriceId(priceId: string): PlanId | null {
  for (const [id, cfg] of Object.entries(PLANS)) {
    if (!cfg.stripe) continue;
    const monthly = process.env[cfg.stripe.monthlyPriceEnvKey];
    const annual = process.env[cfg.stripe.annualPriceEnvKey];
    if (priceId === monthly || priceId === annual) return id as PlanId;
  }
  return null;
}

// ─── UI entitlement metadata (co-located with config for DRY) ───────────────

export type EntitlementArea =
  | 'workspace'
  | 'governance'
  | 'automation'
  | 'insights'
  | 'security';

export interface FeatureDetail {
  label: string;
  description: string;
  area: EntitlementArea;
}

export interface QuotaDetail {
  label: string;
  description: string;
  area: EntitlementArea;
}

export const FEATURE_DETAILS: Record<Feature, FeatureDetail> = {
  githubIntegration: {
    label: 'GitHub Check Runs',
    description:
      'Publish governance results and annotations back to GitHub commits.',
    area: 'automation',
  },
  prComments: {
    label: 'PR review comments',
    description:
      'Post focused inline review comments for deterministic PR findings.',
    area: 'automation',
  },
  apiAccess: {
    label: 'Machine API access',
    description:
      'Let the CLI, CI, agents, and IDE tooling report and query Cloud data.',
    area: 'automation',
  },
  cloudPolicies: {
    label: 'Cloud governance policies',
    description:
      'Manage governance policy from Fragments Cloud instead of local-only config.',
    area: 'governance',
  },
  analytics: {
    label: 'Dashboard analytics',
    description:
      'Track adoption, findings, and design-system health over time.',
    area: 'insights',
  },
  policyManagement: {
    label: 'Policy management',
    description:
      'Edit rules, waivers, and governance behavior for connected repositories.',
    area: 'governance',
  },
  auditLog: {
    label: 'Audit log',
    description:
      'Review append-only activity history for billing, policy, and finding events.',
    area: 'security',
  },
  ssoSaml: {
    label: 'SSO / SAML',
    description:
      'Centralize authentication for larger organizations and security reviews.',
    area: 'security',
  },
  customValidators: {
    label: 'Custom governance rules',
    description:
      'Ingest organization-specific validators beyond the built-in rule set.',
    area: 'governance',
  },
  requiredStatusChecks: {
    label: 'Required status checks',
    description:
      'Use governance as a protected merge gate in GitHub branch protection.',
    area: 'security',
  },
};

export const QUOTA_DETAILS: Record<Quota, QuotaDetail> = {
  maxMembers: {
    label: 'Team members',
    description: 'People who can access this workspace.',
    area: 'workspace',
  },
  maxProjects: {
    label: 'Repositories',
    description: 'Repositories that can be managed in this workspace.',
    area: 'workspace',
  },
  maxRepoBindings: {
    label: 'Repository connections',
    description: 'GitHub repository bindings across all projects.',
    area: 'workspace',
  },
  maxAiSuggestions: {
    label: 'AI suggestions / mo',
    description: 'AI-generated design-system suggestions each billing period.',
    area: 'insights',
  },
  maxEnrichments: {
    label: 'Component enrichments / mo',
    description: 'AI component enrichments each billing period.',
    area: 'insights',
  },
  maxAutofixPrs: {
    label: 'Autofix PRs / mo',
    description: 'Deterministic fix pull requests created each billing period.',
    area: 'automation',
  },
};

export const FEATURE_LABELS: Record<Feature, string> = Object.fromEntries(
  FEATURES.map((feature) => [feature, FEATURE_DETAILS[feature].label]),
) as Record<Feature, string>;

export const QUOTA_LABELS: Record<Quota, string> = Object.fromEntries(
  QUOTAS.map((quota) => [quota, QUOTA_DETAILS[quota].label]),
) as Record<Quota, string>;

export const ENTITLEMENT_AREAS: readonly {
  id: EntitlementArea;
  label: string;
  description: string;
}[] = [
  {
    id: 'workspace',
    label: 'Workspace limits',
    description: 'Members, repositories, and connected sources.',
  },
  {
    id: 'automation',
    label: 'Automation',
    description: 'CI reporting, PR feedback, machine access, and autofix.',
  },
  {
    id: 'governance',
    label: 'Governance controls',
    description: 'Rules, policies, waivers, and validator coverage.',
  },
  {
    id: 'insights',
    label: 'Insights',
    description: 'Analytics and AI-assisted context for teams.',
  },
  {
    id: 'security',
    label: 'Security and compliance',
    description: 'Enterprise controls for auditability and access.',
  },
] as const;

export interface FeatureEntitlement {
  kind: 'feature';
  key: Feature;
  label: string;
  description: string;
  area: EntitlementArea;
  enabled: boolean;
  requiredPlan: PlanId | null;
}

export interface QuotaEntitlement {
  kind: 'quota';
  key: Quota;
  label: string;
  description: string;
  area: EntitlementArea;
  limit: number;
}

export type PlanEntitlement = FeatureEntitlement | QuotaEntitlement;

export function formatQuotaLimit(limit: number): string {
  return Number.isFinite(limit) ? limit.toLocaleString() : 'Unlimited';
}

export function getPlanEntitlements(planId: PlanId): PlanEntitlement[] {
  const plan = PLANS[planId];
  const quotaEntitlements = QUOTAS.map((quota) => ({
    kind: 'quota' as const,
    key: quota,
    label: QUOTA_DETAILS[quota].label,
    description: QUOTA_DETAILS[quota].description,
    area: QUOTA_DETAILS[quota].area,
    limit: plan.quotas[quota],
  }));
  const featureEntitlements = FEATURES.map((feature) => ({
    kind: 'feature' as const,
    key: feature,
    label: FEATURE_DETAILS[feature].label,
    description: FEATURE_DETAILS[feature].description,
    area: FEATURE_DETAILS[feature].area,
    enabled: plan.features[feature],
    requiredPlan: cheapestPlanWithFeature(feature),
  }));
  return [...quotaEntitlements, ...featureEntitlements];
}

export function getPlanEntitlementsByArea(
  planId: PlanId,
): Record<EntitlementArea, PlanEntitlement[]> {
  const grouped: Record<EntitlementArea, PlanEntitlement[]> = {
    workspace: [],
    governance: [],
    automation: [],
    insights: [],
    security: [],
  };
  for (const entitlement of getPlanEntitlements(planId)) {
    grouped[entitlement.area].push(entitlement);
  }
  return grouped;
}

export function getPlanHighlights(planId: PlanId): string[] {
  const plan = PLANS[planId];
  const validatorCount = plan.validators.length;
  return [
    `${formatQuotaLimit(plan.quotas.maxMembers)} team members`,
    `${formatQuotaLimit(plan.quotas.maxProjects)} repositories`,
    `${validatorCount} governance validator${validatorCount === 1 ? '' : 's'}`,
    plan.features.prComments
      ? 'GitHub Check Runs and inline PR comments'
      : 'Local findings and starter governance checks',
    plan.features.apiAccess
      ? 'Machine API for CI, agents, and IDEs'
      : 'Upgrade for machine API and CI reporting',
    `${formatQuotaLimit(plan.quotas.maxAiSuggestions)} AI suggestions / mo`,
  ];
}

/**
 * Curated marketing copy per tier for the pricing cards.
 * Quotas are interpolated from PLANS, so bumping a limit there propagates
 * to the card automatically.
 */
export interface PlanCardCopy {
  /** Optional lead-in like "Everything in Pro, plus:". */
  upgradeFrom?: string;
  /** What to feature on the card — keep to ~6 bullets. */
  bullets: string[];
}

export function getPlanCardCopy(planId: PlanId): PlanCardCopy {
  const plan = PLANS[planId];
  if (planId === 'free') {
    return {
      bullets: [
        `${formatQuotaLimit(plan.quotas.maxMembers)} team members, 1 repository`,
        `All ${plan.validators.length} governance validators`,
        'In-app findings dashboard & triage',
        'Local CLI governance scan',
        'Upgrade to Pro for CI Check Runs, PR comments & the machine API',
        `${formatQuotaLimit(plan.quotas.maxAiSuggestions)} AI suggestions / mo`,
      ],
    };
  }
  if (planId === 'pro') {
    return {
      upgradeFrom: 'Everything in Free, plus:',
      bullets: [
        'GitHub Check Runs & inline PR review comments',
        'Machine API for CI, agents & IDEs',
        `Up to ${formatQuotaLimit(plan.quotas.maxMembers)} members, ${formatQuotaLimit(plan.quotas.maxProjects)} repositories`,
        'Cloud-managed governance policies',
        'Dashboard analytics & adoption tracking',
        `${formatQuotaLimit(plan.quotas.maxAutofixPrs)} autofix PRs / mo`,
      ],
    };
  }
  return {
    upgradeFrom: 'Everything in Pro, plus:',
    bullets: [
      'Unlimited members, repositories, and usage',
      'SSO / SAML',
      'Audit log',
      'Custom governance rules',
      'Required status checks (merge gates)',
      'Dedicated CSM + 99.9% SLA',
    ],
  };
}
